import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Smartphone, Info } from "lucide-react";

const MANAGER_ROLES = new Set(["resident_manager", "housekeeping_manager", "front_office_manager"]);
const PERIODI = [
  { v: 7, label: "7gg" },
  { v: 30, label: "30gg" },
  { v: 90, label: "90gg" },
];

function pctColor(pct) {
  if (pct >= 90) return { bar: "bg-emerald-500", pill: "text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/40" };
  if (pct >= 70) return { bar: "bg-amber-500", pill: "text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/40" };
  return { bar: "bg-rose-500", pill: "text-rose-700 bg-rose-100 dark:text-rose-300 dark:bg-rose-900/40" };
}

/**
 * Compliance Mobile card visibile SOLO al Super Admin per un manager operativo.
 * Guard: currentUserRole === "super_admin" AND targetRole ∈ MANAGER_ROLES.
 */
export default function ManagerDeviceCompliance({ currentUserRole, targetUserId, targetRole }) {
  const [periodo, setPeriodo] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const eligible = currentUserRole === "super_admin" && MANAGER_ROLES.has(targetRole) && !!targetUserId;

  useEffect(() => {
    if (!eligible) return;
    setLoading(true);
    api.get(`/performance/device-compliance/user/${targetUserId}?period_days=${periodo}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eligible, targetUserId, periodo]);

  if (!eligible) return null;

  const pct = data?.mobile_percentage ?? 0;
  const tracked = data?.total_tracked ?? 0;
  const colors = pctColor(pct);

  return (
    <div className="rounded-xl border border-border/60 bg-background/40 p-4 space-y-3" data-testid="manager-device-compliance">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-lavender-500" />
          <h3 className="text-sm font-semibold">Compliance Mobile</h3>
        </div>
        <div className="flex gap-1 rounded-full bg-muted/40 p-0.5">
          {PERIODI.map((p) => (
            <button
              key={p.v}
              type="button"
              onClick={() => setPeriodo(p.v)}
              data-testid={`compliance-periodo-${p.v}`}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${periodo === p.v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-16 rounded-lg bg-muted/40 animate-pulse" />
      ) : tracked === 0 ? (
        <p className="text-xs text-muted-foreground">Nessun controllo tracciato nel periodo selezionato.</p>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl font-medium tabular-nums">{pct}%</span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${colors.pill}`}>
              da mobile / tablet
            </span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-slate-200/70 dark:bg-slate-800/70 overflow-hidden">
            <div className={`h-full ${colors.bar} transition-all duration-500`} style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
          <div className="grid grid-cols-4 gap-2 text-[11px]">
            <Stat label="Mobile" value={data.mobile_count} />
            <Stat label="Tablet" value={data.tablet_count} />
            <Stat label="Desktop" value={data.desktop_count} />
            <Stat label="Non tracc." value={data.untracked_count} muted />
          </div>
        </>
      )}
      <p className="text-[11px] text-muted-foreground leading-snug">
        Percentuale controlli completati da dispositivo mobile o tablet nel periodo selezionato.
      </p>
      <div className="flex items-center gap-1.5 pt-1 border-t border-border/40 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        <Info className="h-3 w-3" /> Visibile solo Super Admin
      </div>
    </div>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div>
      <div className="text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className={`font-medium tabular-nums ${muted ? "text-muted-foreground" : ""}`}>{value}</div>
    </div>
  );
}
